import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function Doc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Company Web Traffic API" description="Provides estimated website traffic analytics for publicly traded companies.
          Data includes global & country rankings, visitor metrics, traffic sources,
          SEO keywords, backlink aut..."  canonicalUrl="https://axionquant.com/docs/02-alternative-data/web-traffic" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Company Web Traffic API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          Provides estimated website traffic analytics for publicly traded companies.
          Data includes global & country rankings, visitor metrics, traffic sources,
          SEO keywords, backlink authority, and peer comparisons. Ideal for competitive
          analysis, investment due diligence, and digital footprint assessment.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get Web Traffic */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /web-traffic/:ticker/traffic
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves detailed website traffic and SEO metrics for the company
                associated with a stock ticker. The endpoint extracts the company's
                primary domain from its profile and queries real-time estimates for
                traffic volume, user engagement, keyword rankings, and more.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol (e.g., "AAPL", "MSFT", "AMZN")
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/web-traffic/AAPL/traffic', '/web-traffic/MSFT/traffic']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">traffic <span className="text-gray-500">object</span></p>
                  <p className="text-sm text-gray-600">Core traffic and engagement metrics</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">visits <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Estimated monthly visits (with M/B suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">change <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Month-over-month visit change (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">pagesPerVisit <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Average pages viewed per visit</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">avgVisitDuration <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Average visit duration (MM:SS)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">bounceRate <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Percentage of single-page visits (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">organicSearchTraffic <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Estimated monthly organic search visits</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">organicSearchTrafficChange <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Change in organic search traffic (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">paidSearchTraffic <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Estimated monthly paid search visits</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">paidSearchTrafficChange <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Change in paid search traffic (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">globalRank <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Global rank among all websites</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">globalRankLocation <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Scope of the global rank (e.g., &quot;Worldwide&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">countryRank <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Country-level rank among all websites</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">countryRankLocation <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Country of the country rank (e.g., &quot;United States&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">visitsInt <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Monthly visits as a raw integer</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">changeNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Month-over-month change as a raw number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">bounceRateNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Bounce rate as a raw percentage number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">organicSearchTrafficInt <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Organic search traffic as a raw integer</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">organicSearchTrafficChangeNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Organic search traffic change as a raw number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">paidSearchTrafficInt <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Paid search traffic as a raw integer</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">paidSearchTrafficChangeNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Paid search traffic change as a raw number</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">last3Months <span className="text-gray-500">array</span></p>
                  <p className="text-sm text-gray-600">Monthly visit totals for the last three months</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">displayValue <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Formatted visit count (with M/B suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">rawValue <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Raw visit count as an integer</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">trafficByCountry <span className="text-gray-500">array</span></p>
                  <p className="text-sm text-gray-600">Traffic breakdown by country</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">country <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Country name</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">trafficShare <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Percentage of total traffic (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">traffic <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Estimated monthly visits from that country</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">desktopShare <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Desktop traffic share in that country (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">mobileShare <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Mobile traffic share in that country (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">trafficShareNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Traffic share as a raw percentage number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">trafficInt <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Traffic as a raw integer</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">desktopShareNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Desktop share as a raw percentage number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">mobileShareNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Mobile share as a raw percentage number</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">trafficJourney <span className="text-gray-500">object</span></p>
                  <p className="text-sm text-gray-600">Referral sources and outbound destinations</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">sources <span className="text-gray-500">array</span></p>
                  <p className="text-sm text-gray-600">Where visitors come from (direct, search, referrals)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">name <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Source name</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">share <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Traffic share from this source (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">change <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Change in traffic share (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">shareNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Traffic share as a raw percentage number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">changeNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Change as a raw number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">destinations <span className="text-gray-500">array</span></p>
                  <p className="text-sm text-gray-600">External sites users go to after visiting</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">name <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Destination site name</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">share <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Outbound traffic share (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">change <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Change in outbound share (with % suffix)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">shareNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Outbound share as a raw percentage number</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4 ml-8">
                  <p className="font-mono text-sm text-gray-800 mb-1">changeNum <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Change as a raw number</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Company Web Traffic" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Company Web Traffic</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="stocks/traffic" />
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="02-alternative-data/web-traffic"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
